import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, Save, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  useTheme,
  ColorPreset,
  ThemeColors,
} from "@/contexts/theme/ThemeContext";
import ColorCategoryPanel from "./ColorCategoryPanel";
import ColorPresets from "./ColorPresets";
import ContrastChecker from "./ContrastChecker";
import { toast } from "sonner";

const ColorManagement = () => {
  const {
    colors,
    updateColor,
    resetColors,
    applyPreset,
    saveColorsToDb,
    loading,
    error,
  } = useTheme();
  const [activeTab, setActiveTab] = useState<"light" | "dark">("light");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      const root = document.documentElement;
      Object.entries(colors[activeTab]).forEach(([key, value]) =>
        root.style.setProperty(`--${key}`, value)
      );
    }
  }, [colors, activeTab, loading]);

  const handleApplyPreset = async (preset: ColorPreset) => {
    setIsSaving(true);
    try {
      await applyPreset(preset);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetColors = async () => {
    setIsSaving(true);
    try {
      await resetColors();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      const currentColors: ThemeColors = {
        light: { ...colors.light },
        dark: { ...colors.dark },
      };
      await saveColorsToDb(currentColors);
      toast.success("All color changes saved successfully");
    } catch {
      toast.error("Failed to save color changes");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Color Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl font-bold">
          Platform Color Management
        </CardTitle>
        <CardDescription>
          Customize the color palette for the entire NationCard™ platform.
          Changes will be applied in real-time to all users.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="light">Light Mode</TabsTrigger>
            <TabsTrigger value="dark">Dark Mode</TabsTrigger>
          </TabsList>
          <TabsContent value="light" className="space-y-4 pt-4">
            <div className="grid gap-6">
              <ColorCategoryPanel
                title="Base Colors"
                description="Primary brand colors used throughout the application"
                colors={[
                  {
                    name: "primary",
                    value: colors.light.primary,
                    label: "Primary",
                  },
                  {
                    name: "accent",
                    value: colors.light.accent,
                    label: "Accent",
                  },
                ]}
                mode="light"
                onColorChange={updateColor}
              />
              <ContrastChecker
                background={colors.light.primary}
                foreground="#FFFFFF"
              />
            </div>
          </TabsContent>
          <TabsContent value="dark" className="space-y-4 pt-4">
            <div className="grid gap-6">
              <ColorCategoryPanel
                title="Base Colors"
                description="Primary brand colors used in dark mode"
                colors={[
                  {
                    name: "primary",
                    value: colors.dark.primary,
                    label: "Primary",
                  },
                  {
                    name: "accent",
                    value: colors.dark.accent,
                    label: "Accent",
                  },
                ]}
                mode="dark"
                onColorChange={updateColor}
              />
              <ContrastChecker
                background={colors.dark.primary}
                foreground="#FFFFFF"
              />
            </div>
          </TabsContent>
        </Tabs>
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium mb-4">Color Presets</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ColorPresets onSelectPreset={handleApplyPreset} />
          </div>
        </div>
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleResetColors}
            disabled={isSaving}
            className="flex items-center"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset to Default
          </Button>
          <Button
            onClick={handleSaveAllChanges}
            disabled={isSaving}
            className="flex items-center"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ColorManagement;
